import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { activitiesApi } from '../api';
import type { Activity, ActivityMutationResponse, DeleteMutationResponse } from '../api/types';
import { objectActivitiesQueryKey } from './useObjectActivities';

// Hook to add attendance
export function useAddAttendance() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: (response_0, variables) => {
        const queryKey = objectActivitiesQueryKey(variables.objectId, variables.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          const withoutOldAttendance = old.filter(_temp2);
          return [...withoutOldAttendance, response_0.activity];
        });
        if (response_0.engagement) {
          queryClient.setQueryData(["activities", "engagement", variables.objectType, variables.objectId], response_0.engagement);
        }
        queryClient.setQueriesData({
          queryKey: ["vote-table", variables.objectType, variables.objectId]
        }, old_0 => {
          if (!old_0) {
            return old_0;
          }
          const firstPage = old_0.pages[0] ?? [];
          const deduplicated = firstPage.filter(a_0 => !(a_0.type === "attendance" && !a_0.deletedAt && String(a_0.actorId) === String(response_0.activity.actorId)));
          return {
            ...old_0,
            pages: [[...deduplicated, response_0.activity], ...old_0.pages.slice(1)]
          };
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to remove attendance
function _temp2(a) {
  return a.type !== "attendance";
}
async function _temp(data) {
  const response = await activitiesApi.addAttendance({
    ...data,
    includeEngagement: true
  });
  if ("activity" in response) {
    return response as ActivityMutationResponse;
  }
  return {
    activity: response as Activity
  };
}
export function useRemoveAttendance() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: result => {
        const queryKey = objectActivitiesQueryKey(result.objectId, result.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          return old.filter(a => {
            if (a.type !== "attendance") {
              return true;
            }
            if (String(a.actorId) !== result.actorId) {
              return true;
            }
            return false;
          });
        });
        if (result.engagement) {
          queryClient.setQueryData(["activities", "engagement", result.objectType, result.objectId], result.engagement);
        }
        queryClient.setQueriesData({
          queryKey: ["vote-table", result.objectType, result.objectId]
        }, old_0 => {
          if (!old_0) {
            return old_0;
          }
          return {
            ...old_0,
            pages: old_0.pages.map(page => page.map(a_0 => a_0.type === "attendance" && String(a_0.actorId) === result.actorId ? {
              ...a_0,
              deletedAt: new Date().toISOString()
            } : a_0))
          };
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Hook to update attendance status (for hosts/admins)
async function _temp3(data) {
  const response = await activitiesApi.removeAttendance(data.objectId, data.objectType, true);
  const engagement = response && typeof response === "object" && "engagement" in response ? (response as DeleteMutationResponse).engagement : undefined;
  return {
    ...data,
    engagement
  };
}
export function useUpdateAttendanceStatus() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: result => {
        const queryKey = objectActivitiesQueryKey(result.objectId, result.objectType);
        queryClient.setQueryData(queryKey, t1 => {
          const old = t1 === undefined ? [] : t1;
          return old.map(a => {
            if (String(a.id) === result.activityId) {
              return result.activity;
            }
            return a;
          });
        });
        queryClient.setQueriesData({
          queryKey: ["vote-table", result.objectType, result.objectId]
        }, old_0 => {
          if (!old_0) {
            return old_0;
          }
          return {
            ...old_0,
            pages: old_0.pages.map(page => page.map(a_0 => String(a_0.id) === result.activityId ? result.activity : a_0))
          };
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp4(data) {
  const updated = await activitiesApi.updateAttendanceStatus(data.activityId, data.status);
  return {
    ...data,
    activity: updated
  };
}